import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

export function StatusBar() {
  const [data, setData] = useState(null);
  const [lastSync, setLastSync] = useState(0);
  const [modeOverride, setModeOverride] = useState(() => localStorage.getItem("trading_mode"));
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const r = await axios.get(`${API}/api/system/health`, { withCredentials: true });
        setData(r.data);
        setLastSync(0);
      } catch (err) {
        void err;
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000);
    timerRef.current = setInterval(() => setLastSync(p => p + 1), 1000);
    return () => { clearInterval(interval); clearInterval(timerRef.current); };
  }, []);

  useEffect(() => {
    const handleModeUpdate = (event) => {
      const mode = event?.detail?.mode;
      if (mode) setModeOverride(mode);
    };
    window.addEventListener("trading-mode-updated", handleModeUpdate);
    return () => window.removeEventListener("trading-mode-updated", handleModeUpdate);
  }, []);

  if (!data) return null;

  const mode = modeOverride || data.trading_mode || "live";
  const brokerStatus = data.services?.broker || (data.broker_connected ? "connected" : "disconnected");
  const brokerLabel = brokerStatus === "connected"
    ? "Connected"
    : (brokerStatus === "degraded" ? "Resting (Off-Hours)" : "Disconnected");
  const brokerColor = brokerStatus === "connected"
    ? "text-emerald-400"
    : (brokerStatus === "degraded" ? "text-cyan-400" : "text-amber-400");
  const brokerTooltip = brokerStatus === "degraded"
    ? "Angel One broker resting outside market hours. Auto-login runs before market open at 08:30 IST."
    : (brokerStatus === "connected" ? "Broker connected and operational" : "Broker disconnected");

  const formatTime = (s) => {
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ago`;
  };

  // Per-exchange market status computed server-side in IST.
  const exchanges = data.market?.exchanges || {};
  const exchangeCodes = ["NSE", "BSE", "MCX"];
  const istTime = data.market?.now_ist_hhmm;

  return (
    <div data-testid="status-bar" className="fixed bottom-0 left-0 right-0 z-50 h-7 bg-zinc-950/95 border-t border-zinc-800 flex items-center justify-center gap-3 sm:gap-5 px-3 sm:px-4 text-[10px] sm:text-[11px] font-mono backdrop-blur-sm overflow-x-auto">
      <span className="text-emerald-400 shrink-0">
        Mode: LIVE
      </span>
      <span className="text-zinc-600 shrink-0">|</span>
      <span className="flex items-center gap-2 shrink-0" data-testid="market-status">
        {istTime && <span className="text-zinc-500 hidden sm:inline">{istTime} IST</span>}
        {exchangeCodes.map((code, i) => {
          const open = Boolean(exchanges[code]?.open);
          return (
            <span key={code} className="flex items-center gap-1">
              {i > 0 && <span className="text-zinc-700">·</span>}
              <span className={open ? "text-emerald-400" : "text-zinc-500"}>
                {code}: {open ? "OPEN" : "CLOSED"}
              </span>
            </span>
          );
        })}
      </span>
      <span className="text-zinc-600">|</span>
      <span className="text-zinc-400">Sync: {formatTime(lastSync)}</span>
      <span className="text-zinc-600">|</span>
      <span className={brokerColor} title={brokerTooltip}>
        Broker: {brokerLabel}
      </span>
      <span className="text-zinc-600">|</span>
      <span className="text-zinc-400">v{data.version || "4.1"}</span>
    </div>
  );
}
